import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Descriptions, Divider, Dropdown, Menu, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import {
  BookOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  CopyOutlined,
  DownloadOutlined,
  EllipsisOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  PaperClipOutlined,
  ShareAltOutlined,
  ToolOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AgentTask, AgentStep, Artifact, ToolCallLog } from '../types';
import DurationBadge, { calcMs } from './DurationBadge';
import * as api from '../api/client';
import ArtifactDeliveryList from './ArtifactDeliveryList';
import { requestProfileConnectorsNavigation } from '../api/artifactDelivery';
import KnowledgeBaseEntryDrawer from './KnowledgeBaseEntryDrawer';

const { Text, Title } = Typography;

interface Props {
  task: AgentTask;
  steps: AgentStep[];
  artifacts: Artifact[];
  toolCalls: ToolCallLog[];
}

const PILL_BORDER_COLOR = '#3f3f3f';
const PILL_BACKGROUND = '#2a2a2a';
const PILL_MENU_ITEM_STYLE: React.CSSProperties = { color: '#d1d5db', borderRadius: 20, margin: '4px 6px' };
const PILL_MENU_DIVIDER_STYLE: React.CSSProperties = { borderColor: PILL_BORDER_COLOR, margin: '4px 10px' };
const PRAXYS_PRODUCTION_ORIGIN = 'https://praxys.ai';

const getPillButtonStyle = (color: string): React.CSSProperties => ({
  background: PILL_BACKGROUND,
  borderColor: PILL_BORDER_COLOR,
  borderRadius: 20,
  color,
  fontSize: 11,
});

const taskRequestsDownloadableDocx = (goal: string): boolean => {
  const normalized = (goal || '').toLowerCase();
  if (!normalized) return false;
  const docTerms = ['docx', '.docx', 'word document', 'word doc', 'word file', 'microsoft word'];
  const docRequestPhrases = ['as a docx', 'in docx', 'to docx', 'as word', 'in a word document', 'in a word file'];
  const intentTerms = ['download', 'downloadable', 'export', 'save', 'generate', 'create', 'provide', 'attach', 'send'];
  const mentionsDocx = docTerms.some(term => normalized.includes(term));
  const mentionsFormatRequest = docRequestPhrases.some(term => normalized.includes(term));
  const mentionsDeliveryIntent = intentTerms.some(term => normalized.includes(term));
  return mentionsDocx && (mentionsFormatRequest || mentionsDeliveryIntent);
};

const currentAppOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin && !/yourdomain/i.test(window.location.origin)) {
    return window.location.origin;
  }
  return PRAXYS_PRODUCTION_ORIGIN;
};

const sanitizeResultForDisplay = (value: string): string => {
  if (!value) return '';
  const appOrigin = currentAppOrigin();
  let result = value;

  result = result.replace(
    /https?:\/\/(?:www\.)?yourdomain(?:\.[a-z0-9.-]+)?([^\s)\]}>"']*)/gi,
    (_match, path: string) => `${appOrigin}${path || ''}`,
  );

  result = result.replace(
    /^\s*["']?storage_path["']?\s*[:=]\s*["']?[^\r\n,"'}]+["']?\s*,?\s*$/gim,
    '',
  );

  result = result.replace(
    /(?:\/data\/artifacts\/|\/data\/tasks\/|\/tmp\/praxys[^/\s]*\/)([^\s)\]}>"']+)/gi,
    (_match, filename: string) => filename.split('/').pop() || 'generated-artifact',
  );

  return result.replace(/\n{3,}/g, '\n\n').trim();
};

const FinalResultPanel: React.FC<Props> = ({ task, steps, artifacts, toolCalls }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resultCopied, setResultCopied] = useState(false);
  const [kbDrawerOpen, setKbDrawerOpen] = useState(false);
  const [addedKbName, setAddedKbName] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  const [savingToDrive, setSavingToDrive] = useState(false);

  const displayAnswer = useMemo(
    () => sanitizeResultForDisplay(task.final_answer || ''),
    [task.final_answer],
  );

  useEffect(() => {
    let cancelled = false;
    if (!task.id || !task.user_id) return undefined;
    api.listKnowledgeBaseEntriesBySource('task_result', task.id)
      .then((res) => {
        if (cancelled) return;
        const entry = (res.data.entries || [])[0];
        if (entry) setAddedKbName(entry.knowledge_base_name || 'Added to Knowledge Base');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [task.id, task.user_id]);

  if (!['COMPLETED', 'FAILED', 'CANCELLED'].includes(task.status)) return null;

  const completedSteps = steps.filter(s => s.status === 'COMPLETED').length;
  const failedSteps = steps.filter(s => s.status === 'FAILED').length;
  const totalMs = calcMs(task.started_at, task.completed_at);
  const hasDocxArtifact = artifacts.some(a =>
    a.content_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
  const showDocxDownload = taskRequestsDownloadableDocx(task.goal) || hasDocxArtifact;
  const toolsUsed = toolCalls.map(tc => tc.tool_name).filter((v, i, arr) => arr.indexOf(v) === i);
  const isSuccess = task.status === 'COMPLETED';
  const safeId = task.id.replace(/[^a-zA-Z0-9_-]/g, '_');
  const isAddedToKnowledgeBase = addedKbName.trim().length > 0;

  const handleDownloadMd = () => {
    if (!displayAnswer) return;
    const blob = new Blob([displayAnswer], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `praxys-result-${safeId}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleDownloadDocx = () => {
    void api.downloadTaskExport(task.id, 'docx', `praxys-result-${safeId}.docx`);
  };

  const handleDownloadPdf = () => {
    void api.downloadTaskExport(task.id, 'pdf', `praxys-result-${safeId}.pdf`);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCopyResult = () => {
    if (!displayAnswer) return;
    navigator.clipboard.writeText(displayAnswer).then(() => {
      setResultCopied(true);
      setTimeout(() => setResultCopied(false), 2000);
    });
  };

  const handleSendResultToDrive = async () => {
    if (!displayAnswer) return;
    setSavingToDrive(true);
    try {
      const status = await api.getGDriveStatus(task.user_id);
      const instance = status.data.instance;
      if (!status.data.connected || !instance) {
        requestProfileConnectorsNavigation();
        return;
      }
      const safeName = (task.goal || task.id || 'task-result')
        .replace(/[^a-zA-Z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80) || safeId;
      const file = new File(
        [displayAnswer],
        `praxys-result-${safeName}.md`,
        { type: 'text/markdown;charset=utf-8' },
      );
      await api.uploadFileToConnector(instance.id, file, 'task', task.id, task.goal || task.id);
      Modal.success({
        title: 'Saved to Drive',
        content: 'The task result was saved under Praxys.ai/Tasks in Google Drive.',
      });
    } catch (err: any) {
      Modal.error({
        title: 'Send to Drive failed',
        content: err?.response?.data?.detail || err?.message || 'Could not save this result to Google Drive.',
      });
    } finally {
      setSavingToDrive(false);
    }
  };

  const ActionBar = () => (
    <Space size={4} wrap>
      <Button
        size="small"
        icon={<BookOutlined />}
        onClick={() => setKbDrawerOpen(true)}
        disabled={isAddedToKnowledgeBase}
        style={getPillButtonStyle('#58a6ff')}
      >
        {isAddedToKnowledgeBase ? addedKbName : 'Add to Knowledge Base'}
      </Button>
      <Tooltip title="Download Markdown">
        <Button size="small" icon={<FileTextOutlined />} onClick={handleDownloadMd} style={getPillButtonStyle('#10b981')}>
          MD
        </Button>
      </Tooltip>
      {showDocxDownload && (
        <Tooltip title="Download Word Document">
          <Button size="small" icon={<FileWordOutlined />} onClick={handleDownloadDocx} style={getPillButtonStyle('#10b981')}>
            Docx
          </Button>
        </Tooltip>
      )}
      <Tooltip title="Download PDF">
        <Button size="small" icon={<FilePdfOutlined />} onClick={handleDownloadPdf} style={getPillButtonStyle('#10b981')}>
          PDF
        </Button>
      </Tooltip>
      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
        <Button
          size="small"
          icon={copied ? <CheckCircleOutlined /> : <CopyOutlined />}
          onClick={handleShare}
          style={getPillButtonStyle(copied ? '#52c41a' : '#58a6ff')}
        >
          Share
        </Button>
      </Tooltip>
      <Tooltip title={resultCopied ? 'Copied!' : 'Copy result'}>
        <Button
          size="small"
          icon={resultCopied ? <CheckCircleOutlined /> : <CopyOutlined />}
          onClick={handleCopyResult}
          style={getPillButtonStyle(resultCopied ? '#52c41a' : '#58a6ff')}
        >
          Copy
        </Button>
      </Tooltip>
      <Tooltip title="Send this task result to Google Drive">
        <Button
          size="small"
          icon={<CloudUploadOutlined />}
          onClick={handleSendResultToDrive}
          loading={savingToDrive}
          style={getPillButtonStyle('#38bdf8')}
        >
          Send to Drive
        </Button>
      </Tooltip>
    </Space>
  );

  const dropdownMenu = (
    <Menu
      theme="dark"
      style={{ background: PILL_BACKGROUND, border: `1px solid ${PILL_BORDER_COLOR}`, borderRadius: 12, minWidth: 160 }}
      onClick={({ key }) => {
        if (key === 'preview') setPreviewOpen(true);
        if (key === 'share') handleShare();
        if (key === 'md') handleDownloadMd();
        if (key === 'docx') handleDownloadDocx();
        if (key === 'pdf') handleDownloadPdf();
        if (key === 'drive') void handleSendResultToDrive();
      }}
    >
      <Menu.Item key="preview" icon={<EyeOutlined />} style={PILL_MENU_ITEM_STYLE}>Preview</Menu.Item>
      <Menu.Divider style={PILL_MENU_DIVIDER_STYLE} />
      <Menu.Item key="share" icon={<ShareAltOutlined />} style={PILL_MENU_ITEM_STYLE}>
        {copied ? 'Link copied!' : 'Share (copy link)'}
      </Menu.Item>
      <Menu.Item key="drive" icon={<CloudUploadOutlined />} style={PILL_MENU_ITEM_STYLE}>Send to Drive</Menu.Item>
      <Menu.Divider style={PILL_MENU_DIVIDER_STYLE} />
      <Menu.SubMenu key="download" title="Download" icon={<DownloadOutlined />} style={PILL_MENU_ITEM_STYLE}>
        <Menu.Item key="md" icon={<FileTextOutlined />} style={PILL_MENU_ITEM_STYLE}>Markdown (.md)</Menu.Item>
        {showDocxDownload && (
          <Menu.Item key="docx" icon={<FileWordOutlined />} style={PILL_MENU_ITEM_STYLE}>Word (.docx)</Menu.Item>
        )}
        <Menu.Item key="pdf" icon={<FilePdfOutlined />} style={PILL_MENU_ITEM_STYLE}>PDF</Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );

  return (
    <>
      <Card
        style={{
          background: isSuccess ? PILL_BACKGROUND : '#1f0f0f',
          borderColor: isSuccess ? PILL_BORDER_COLOR : '#5c2626',
          marginTop: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: collapsed ? 0 : 12 }}>
          <Space
            align="center"
            onClick={() => setCollapsed(prev => !prev)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            role="button"
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand task result' : 'Collapse task result'}
          >
            {collapsed
              ? <CaretRightOutlined style={{ color: '#9ca3af', fontSize: 12 }} />
              : <CaretDownOutlined style={{ color: '#9ca3af', fontSize: 12 }} />}
            {isSuccess
              ? <TrophyOutlined style={{ color: '#ffd700', fontSize: 22 }} />
              : <CheckCircleOutlined style={{ color: '#ff4d4f', fontSize: 22 }} />}
            <Title level={5} style={{ color: isSuccess ? '#52c41a' : '#ff4d4f', margin: 0 }}>
              {isSuccess ? 'Task Completed…' : task.status === 'FAILED' ? 'Task Failed' : 'Task Cancelled'}
            </Title>
            {totalMs != null && <DurationBadge ms={totalMs} />}
          </Space>
          {displayAnswer && !collapsed && (
            <Dropdown overlay={dropdownMenu} trigger={['click']} placement="bottomRight">
              <Button size="small" icon={<EllipsisOutlined />} style={{ ...getPillButtonStyle('#9ca3af'), fontSize: 16, lineHeight: 1 }} />
            </Dropdown>
          )}
        </div>

        {!collapsed && (
          <>
            {displayAnswer && (
              <div
                style={{
                  background: PILL_BACKGROUND,
                  border: `1px solid ${PILL_BORDER_COLOR}`,
                  borderRadius: 6,
                  padding: '10px 14px',
                  marginBottom: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, gap: 8, flexWrap: 'wrap' }}>
                  <Text style={{ color: '#58a6ff', fontSize: 12, fontWeight: 600 }}>Final Answer</Text>
                  <ActionBar />
                </div>
                <div className="praxys-markdown-result">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayAnswer}</ReactMarkdown>
                </div>
              </div>
            )}

            <Divider style={{ borderColor: PILL_BORDER_COLOR, margin: '10px 0' }} />

            <Descriptions
              size="small"
              column={2}
              labelStyle={{ color: '#555', fontSize: 11 }}
              contentStyle={{ color: '#e6edf3', fontSize: 12 }}
            >
              <Descriptions.Item label="Steps completed">
                {completedSteps} / {steps.length}
                {failedSteps > 0 && <Tag color="error" style={{ fontSize: 10, marginLeft: 4 }}>{failedSteps} failed</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Artifacts">
                {artifacts.length > 0 ? <><PaperClipOutlined style={{ color: '#1890ff' }} /> {artifacts.length}</> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Tool calls">
                {toolCalls.length > 0 ? <><ToolOutlined style={{ color: '#a855f7' }} /> {toolCalls.length}</> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Duration">
                {totalMs != null ? <DurationBadge ms={totalMs} /> : '—'}
              </Descriptions.Item>
            </Descriptions>

            <ArtifactDeliveryList artifacts={artifacts} />

            {toolsUsed.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text style={{ color: '#555', fontSize: 11 }}>Tools used: </Text>
                {toolsUsed.map(t => (
                  <span
                    key={t}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      background: '#2a2a2a',
                      border: '1px solid #3f3f3f',
                      borderRadius: 20,
                      padding: '3px 12px',
                      color: '#10b981',
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    <CheckCircleOutlined style={{ fontSize: 12 }} />
                    {t}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={860}
        bodyStyle={{ padding: 0, background: '#0d1117' }}
        style={{ top: 40 }}
        closeIcon={null}
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#161b22',
              borderBottom: '1px solid #30363d',
              padding: '10px 16px',
              margin: '-16px -24px 0',
            }}
          >
            <Text style={{ color: '#58a6ff', fontWeight: 600 }}>Preview — Final Answer</Text>
            <Space>
              <ActionBar />
              <Button size="small" icon={<CloseOutlined />} onClick={() => setPreviewOpen(false)} style={getPillButtonStyle('#9ca3af')} />
            </Space>
          </div>
        }
      >
        <div style={{ background: '#0d1117', padding: '16px 24px 24px', maxHeight: '75vh', overflowY: 'auto' }}>
          <div className="praxys-markdown-result">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayAnswer}</ReactMarkdown>
          </div>
        </div>
      </Modal>

      <KnowledgeBaseEntryDrawer
        open={kbDrawerOpen}
        onClose={() => setKbDrawerOpen(false)}
        onSaved={({ knowledgeBaseName }) => {
          const nextName = (knowledgeBaseName || '').trim();
          setAddedKbName(nextName || 'Added to Knowledge Base');
        }}
        userId={task.user_id}
        draft={{
          suggestedTitle: task.goal?.slice(0, 120) || 'Task Result',
          summary: displayAnswer.slice(0, 600),
          contentMarkdown: displayAnswer,
          sourceModule: 'task_result',
          sourceId: task.id,
          tags: ['task-result', (task.agent_type || '').toLowerCase()].filter(Boolean),
          artifactIds: artifacts.map((a) => a.id),
          metadata: {
            task_id: task.id,
            task_status: task.status,
            tool_call_count: toolCalls.length,
            completed_at: task.completed_at,
          },
        }}
      />
    </>
  );
};

export default FinalResultPanel;
